'use client';

import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utilities/classname';
import { buttonCva } from '../utils/button-classnames';
import { Portal } from './portal';
import type { ExtractPageBlock } from '../render-page/types/block.types';

export type EmbedButtonBlock = ExtractPageBlock<'embed-button'>;

/**
 * A themed button that opens an external page in a modal iframe. The iframe is
 * mounted only while the modal is open — it isn't fetched until the visitor
 * opens the modal, and it's unmounted again on close (so nothing keeps running
 * in the background and each open is a fresh load). Mirrors the form-button view
 * shape so it slots into the shared button render paths (CTAs, navbar, …).
 */
export const EmbedButtonBlockView: React.FC<{
  block: EmbedButtonBlock;
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'lg';
  /** Server-rendered label (with prepend/append icons). Falls back to plain text. */
  label?: React.ReactNode;
}> = ({ block, onClick, className, size, label }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [opened, setOpened] = useState(false);

  const open = () => {
    onClick?.();
    setOpened(true);
    dialogRef.current?.showModal();
  };

  return (
    <>
      <button
        type="button"
        className={cn(buttonCva({ ...block['button-theme'], size }), className)}
        onClick={open}
      >
        {label ?? block.text}
      </button>

      <Portal>
        <dialog
          ref={dialogRef}
          onClose={() => setOpened(false)}
          className="modal bg-base-content/50 backdrop-blur-xs"
        >
          <div className="modal-box w-full max-w-2xl overflow-hidden p-0">
            <form method="dialog">
              <button className="btn btn-sm btn-ghost btn-square absolute right-2 top-2 z-10">
                <X />
                <span className="sr-only">Close</span>
              </button>
            </form>

            {opened ? (
              <iframe
                src={block.url}
                title={block.title || block.text || undefined}
                height={block.height ?? 440}
                referrerPolicy="strict-origin-when-cross-origin"
                className="block w-full border-0"
              />
            ) : null}
          </div>
        </dialog>
      </Portal>
    </>
  );
};
